import React from 'react';
import dayjs from 'dayjs';

import {
  AssetInventorySummary,
  FindingRow,
} from '@totem/components/services/assetInventory/v2/types';

export type Severity = 'Critical' | 'High' | 'Medium' | 'Low';
export type CheckGroup = 'security' | 'recovery' | 'coverage' | 'records';

export const SEVERITIES: Severity[] = ['Critical', 'High', 'Medium', 'Low'];

export const SEVERITY_DETAILS: Record<
  Severity,
  { color: string; pill: string; hint: string }
> = {
  Critical: { color: '#ef4444', pill: 'pill-red', hint: 'Act now' },
  High: { color: '#f97316', pill: 'pill-orange', hint: 'This quarter' },
  Medium: { color: '#f59e0b', pill: 'pill-amber', hint: 'Schedule' },
  Low: { color: '#3b82f6', pill: 'pill-blue', hint: 'Record keeping' },
};

export const CHECK_GROUPS: { key: CheckGroup | 'all'; label: string }[] = [
  { key: 'all', label: 'All checks' },
  { key: 'security', label: 'Security & support' },
  { key: 'recovery', label: 'Recovery' },
  { key: 'coverage', label: 'Coverage & service' },
  { key: 'records', label: 'Record quality' },
];

export const NO_ACTION_NEEDED = 'None needed.';

export const formatDay = (date: string | null) =>
  date ? dayjs(date).format('D MMM YYYY') : '—';

export const formatMonth = (date: string | null) =>
  date ? dayjs(date).format('MMM YYYY') : '—';

// Support and contract end dates are calendar dates stored at midnight UTC; formatting them in local time would show
// the day before, so only their date part is used.
const calendarDate = (date: string) => dayjs(date.slice(0, 10));

export const formatCalendarDay = (date: string | null) =>
  date ? calendarDate(date).format('D MMM YYYY') : '—';

export const formatCalendarMonth = (date: string | null) =>
  date ? calendarDate(date).format('MMM YYYY') : '—';

export type CheckColumn = {
  title: string;
  render: (row: FindingRow) => React.ReactNode;
  // What the column sorts on; dates and counts sort as values rather than as their display text.
  sortValue?: (row: FindingRow) => string | number;
  managed?: boolean;
};

export type TableRows = {
  columns: string[];
  rows: (string | number)[][];
};

const timeOf = (date: string | null) => (date ? dayjs(date).valueOf() : 0);

export interface CheckDefinition {
  key: string;
  severity: Severity;
  group: CheckGroup;
  unit: (count: number) => string;
  title: string;
  why: (summary: AssetInventorySummary, count: number) => string;
  ibWillDo: string;
  needFromYou: string;
  passed: (summary: AssetInventorySummary) => string;
  // Device checks list their named assets; the others build their own table from the summary.
  columns?: CheckColumn[];
  table?: (summary: AssetInventorySummary) => TableRows;
  count?: (summary: AssetInventorySummary) => number;
  // Device checks open the Details tab filtered to the check; false for checks that aren't per device.
  detailsFilter?: string | false;
  appliesTo?: (summary: AssetInventorySummary) => boolean;
}

const plural = (single: string, many: string) => (count: number) =>
  count === 1 ? single : many;

const asset = plural('asset', 'assets');
const computer = plural('computer', 'computers');

const nameColumn: CheckColumn = {
  title: 'Asset',
  render: (row) => row.name,
  sortValue: (row) => row.name,
};
const buildingColumn: CheckColumn = {
  title: 'Building',
  render: (row) => row.building || '—',
  sortValue: (row) => row.building,
};
const managedColumn: CheckColumn = {
  title: 'Mgmt',
  render: () => null,
  sortValue: (row) => (row.isManaged ? 1 : 0),
  managed: true,
};
const typeColumn: CheckColumn = {
  title: 'Type',
  render: (row) => row.type || '—',
  sortValue: (row) => row.type,
};
const lastBackupColumn: CheckColumn = {
  title: 'Last backup',
  render: (row) => formatDay(row.lastBackup),
  sortValue: (row) => timeOf(row.lastBackup),
};

// Names what the support date applies to: the operating system, or the hardware product type.
const supportProduct = (row: FindingRow) => {
  if (!row.endOfSupportProduct) {
    return row.operatingSystem || row.type;
  }
  return row.endOfSupportProduct === 'Operating System'
    ? row.operatingSystem || row.endOfSupportProduct
    : row.endOfSupportProduct;
};

const supportDetail = (row: FindingRow) =>
  [
    supportProduct(row),
    row.endOfSupport && `ended ${formatCalendarMonth(row.endOfSupport)}`,
  ]
    .filter(Boolean)
    .join(' · ');

export const CHECKS: CheckDefinition[] = [
  {
    key: 'pastEndOfSupport',
    severity: 'Critical',
    group: 'security',
    unit: asset,
    title: 'Assets past end of support',
    why: (summary) =>
      `The manufacturer no longer issues security fixes. ${summary.totals.pastEndOfSupportManaged} of these are under IB management.`,
    ibWillDo:
      'IB will confirm a replacement path for each and add them to the budget outlook.',
    needFromYou: 'Approve replacements in the next capital cycle.',
    passed: () => 'No assets past end of support',
    columns: [
      nameColumn,
      buildingColumn,
      {
        title: 'Detail',
        render: supportDetail,
        sortValue: (row) => timeOf(row.endOfSupport),
      },
      managedColumn,
    ],
  },
  {
    key: 'lowDiskSpace',
    severity: 'Critical',
    group: 'recovery',
    unit: asset,
    title: 'Storage almost full',
    why: () =>
      'When a disk fills, recording and logging stop and the system can fail.',
    ibWillDo: 'IB to clear or expand storage this week.',
    needFromYou: NO_ACTION_NEEDED,
    passed: () => 'No storage close to full',
    columns: [
      nameColumn,
      buildingColumn,
      {
        title: 'Detail',
        render: (row) =>
          row.minFreePercent === null
            ? 'Below alert threshold'
            : `${row.minFreePercent < 1 ? 'under 1' : row.minFreePercent}% free`,
        sortValue: (row) => row.minFreePercent ?? -1,
      },
      managedColumn,
    ],
  },
  {
    key: 'noBackup',
    severity: 'High',
    group: 'recovery',
    unit: computer,
    title: 'Computers with no backup',
    why: (summary, count) => {
      const { computers, servers } = summary.totals;
      const workstations = computers - servers;
      return `${count} of ${workstations} computers have never reported a backup.`;
    },
    ibWillDo: 'IB to enrol the managed machines.',
    needFromYou:
      'Decide whether the unmanaged ones should come under management.',
    passed: () => 'Every computer has reported a backup',
    columns: [
      nameColumn,
      buildingColumn,
      {
        title: 'Detail',
        render: (row) => `${row.type} · no backup`,
        sortValue: (row) => row.type,
      },
      managedColumn,
    ],
    appliesTo: (summary) =>
      summary.totals.computers - summary.totals.servers > 0,
  },
  {
    key: 'serverBackup',
    severity: 'High',
    group: 'recovery',
    unit: plural('server', 'servers'),
    title: 'Servers without a backup in the last day',
    why: (summary, count) =>
      `${count} of ${summary.totals.servers} servers have no recovery point from the last day.`,
    ibWillDo: 'IB to restore the daily backup schedule.',
    needFromYou: NO_ACTION_NEEDED,
    passed: (summary) =>
      `All ${summary.totals.servers} servers backed up within the last day`,
    columns: [nameColumn, buildingColumn, lastBackupColumn, managedColumn],
    appliesTo: (summary) => summary.totals.servers > 0,
  },
  {
    key: 'staleBackup',
    severity: 'High',
    group: 'recovery',
    unit: computer,
    title: 'Backups older than 7 days',
    why: () => 'A backup exists but the latest recovery point is out of date.',
    ibWillDo: 'IB to restore the backup schedule.',
    needFromYou: 'Confirm whether unmanaged machines are still needed.',
    passed: () => 'Every backup is less than 7 days old',
    columns: [nameColumn, buildingColumn, lastBackupColumn, managedColumn],
    appliesTo: (summary) => summary.totals.computers > 0,
  },
  {
    key: 'vulnerabilities',
    severity: 'High',
    group: 'security',
    unit: asset,
    title: 'Assets with open vulnerabilities',
    why: () =>
      'Known software weaknesses. Network segmentation limits who can reach these assets, but they still need patching.',
    ibWillDo:
      'IB to prioritise patching, starting with the most exposed assets.',
    needFromYou: 'Approve maintenance windows so patching can happen.',
    passed: () => 'No open vulnerabilities',
    columns: [
      nameColumn,
      buildingColumn,
      {
        title: 'Detail',
        render: (row) =>
          `${row.openVulns} known issue${row.openVulns === 1 ? '' : 's'}`,
        sortValue: (row) => row.openVulns,
      },
      managedColumn,
    ],
  },
  {
    key: 'serviceContractEnding',
    severity: 'High',
    group: 'coverage',
    unit: plural('contract', 'contracts'),
    title: 'Service contracts expired or ending soon',
    why: () => 'These support contracts have expired or end within 60 days.',
    ibWillDo:
      'IB to confirm current coverage with each vendor and update the record.',
    needFromYou: 'Renew or replace contracts that are still needed.',
    passed: () => 'No service contracts expired or ending soon',
    columns: [
      nameColumn,
      {
        title: 'Vendor',
        render: (row) => row.serviceCompany || '—',
        sortValue: (row) => row.serviceCompany,
      },
      {
        title: 'Ends',
        render: (row) =>
          dayjs(row.serviceEnd).isBefore(dayjs())
            ? `Expired ${formatCalendarDay(row.serviceEnd)}`
            : formatCalendarDay(row.serviceEnd),
        sortValue: (row) => timeOf(row.serviceEnd),
      },
      managedColumn,
    ],
  },
  {
    key: 'noAntiVirus',
    severity: 'High',
    group: 'security',
    unit: computer,
    title: 'No anti-virus detected',
    why: () => 'No endpoint protection is reporting from these computers.',
    ibWillDo: 'IB to deploy protection.',
    needFromYou: NO_ACTION_NEEDED,
    passed: () => 'Every computer has anti-virus protection',
    columns: [nameColumn, buildingColumn, managedColumn],
    appliesTo: (summary) => summary.totals.computers > 0,
  },
  {
    key: 'notManaged',
    severity: 'Medium',
    group: 'coverage',
    unit: asset,
    title: 'Assets not under IB management',
    why: () => "On the network but outside IB's contract.",
    ibWillDo: 'IB to recommend which to bring under management.',
    needFromYou: 'Decide: manage, replace or retire.',
    passed: () => 'Every asset is under IB management',
    table: (summary) => ({
      columns: ['Type', 'Count'],
      rows: summary.notManagedByType.map((item) => [
        item.label || 'Not recorded',
        item.count,
      ]),
    }),
  },
  {
    key: 'noServiceContract',
    severity: 'Medium',
    group: 'coverage',
    unit: asset,
    title: 'No service contract on file',
    why: (summary) =>
      `Only ${summary.totals.withServiceContract} of ${summary.totals.assets} assets have a support contract recorded. Coverage is usually one contract per building system, so record it there and every device inherits it.`,
    ibWillDo: 'IB to collect contract details by building system.',
    needFromYou:
      'Send IB copies of controls, fire and access service agreements.',
    passed: () => 'Every asset has a service contract on file',
    columns: [nameColumn, buildingColumn, typeColumn, managedColumn],
  },
  {
    key: 'newDevice',
    severity: 'Medium',
    group: 'security',
    unit: asset,
    title: 'New devices in the last 30 days',
    why: () =>
      'New equipment on a building network should be expected. These were found by monitoring.',
    ibWillDo: 'IB to identify each and confirm with site staff.',
    needFromYou: 'Confirm who installed them.',
    passed: () => 'No unexpected new devices in the last 30 days',
    columns: [
      nameColumn,
      buildingColumn,
      {
        title: 'First seen',
        render: (row) => formatDay(row.createdAt),
        sortValue: (row) => timeOf(row.createdAt),
      },
      managedColumn,
    ],
  },
  {
    key: 'noMaintenanceWindow',
    severity: 'Medium',
    group: 'coverage',
    unit: computer,
    title: 'No maintenance window agreed',
    why: () => 'Without an agreed window, patches and reboots get postponed.',
    ibWillDo: 'IB to propose windows per building.',
    needFromYou: 'Agree windows with building engineering.',
    passed: () => 'Every managed computer has a maintenance window',
    columns: [nameColumn, buildingColumn, managedColumn],
  },
  {
    key: 'endOfSupportSoon',
    severity: 'Medium',
    group: 'security',
    unit: asset,
    title: 'End of support within 12 months',
    why: () => 'Plan replacement before support ends.',
    ibWillDo: 'IB to add to the capital plan.',
    needFromYou: 'Budget for replacement.',
    passed: () => 'Nothing reaches end of support in the next 12 months',
    columns: [
      nameColumn,
      buildingColumn,
      {
        title: 'Support ends',
        render: (row) =>
          [formatCalendarDay(row.endOfSupport), supportProduct(row)]
            .filter(Boolean)
            .join(' · '),
        sortValue: (row) => timeOf(row.endOfSupport),
      },
      managedColumn,
    ],
  },
  {
    key: 'endOfSupportNotMarked',
    severity: 'Medium',
    group: 'records',
    unit: asset,
    title: 'Support end date passed but not marked',
    why: () => 'The date is past, yet the record still says supported.',
    ibWillDo:
      'IB to calculate support status from the date. These may move to Critical.',
    needFromYou: NO_ACTION_NEEDED,
    passed: () => 'Every passed support date is marked',
    columns: [
      nameColumn,
      buildingColumn,
      {
        title: 'Detail',
        render: (row) =>
          [supportProduct(row), `ended ${formatCalendarDay(row.endOfSupport)}`]
            .filter(Boolean)
            .join(' · '),
        sortValue: (row) => timeOf(row.endOfSupport),
      },
      managedColumn,
    ],
  },
  {
    key: 'duplicateIp',
    severity: 'Medium',
    group: 'records',
    unit: plural('conflict', 'conflicts'),
    title: 'Duplicate IP addresses',
    why: () =>
      'Two devices in one building claim one address, which causes dropouts.',
    ibWillDo: 'IB to correct addressing.',
    needFromYou: NO_ACTION_NEEDED,
    passed: () => 'No duplicate IP addresses within a building',
    count: (summary) => summary.duplicateIps.length,
    detailsFilter: false,
    table: (summary) => ({
      columns: ['IP', 'Building', 'Assets'],
      rows: summary.duplicateIps.map((item) => [
        item.ipAddress,
        item.building || '—',
        item.devices.join(', '),
      ]),
    }),
  },
  {
    key: 'noOperatingSystem',
    severity: 'Medium',
    group: 'records',
    unit: computer,
    title: 'Computers with no operating system recorded',
    why: () => 'Cannot be checked for patches or end of support.',
    ibWillDo: 'IB to complete the records.',
    needFromYou: NO_ACTION_NEEDED,
    passed: () => 'Every computer has an operating system recorded',
    columns: [nameColumn, buildingColumn, typeColumn, managedColumn],
    appliesTo: (summary) => summary.totals.computers > 0,
  },
  {
    key: 'noIpAddress',
    severity: 'Medium',
    group: 'records',
    unit: asset,
    title: 'No IP address recorded',
    why: () => 'Cannot be monitored or found on the network.',
    ibWillDo: 'IB to complete the records.',
    needFromYou: NO_ACTION_NEEDED,
    passed: () => 'Every asset has an IP address recorded',
    columns: [nameColumn, buildingColumn, typeColumn, managedColumn],
  },
  {
    key: 'notLinked',
    severity: 'Low',
    group: 'records',
    unit: asset,
    title: 'Not linked to a building system',
    why: () =>
      "These assets can't be grouped by what they support or inherit a service contract.",
    ibWillDo: 'IB to link them.',
    needFromYou: NO_ACTION_NEEDED,
    passed: () => 'Every asset is linked to a building system',
    columns: [nameColumn, buildingColumn, typeColumn, managedColumn],
  },
  {
    key: 'unclearType',
    severity: 'Low',
    group: 'records',
    unit: asset,
    title: 'Unclear device type',
    why: () =>
      'Generic labels such as IOT_device make the inventory harder to use.',
    ibWillDo: 'IB to standardise device types.',
    needFromYou: NO_ACTION_NEEDED,
    passed: () => 'Every asset has a clear device type',
    table: (summary) => ({
      columns: ['Type label', 'Count'],
      rows: summary.unclearTypes.map((item) => [
        item.label || 'Not recorded',
        item.count,
      ]),
    }),
  },
];

export const getCheckCount = (
  check: CheckDefinition,
  summary: AssetInventorySummary,
) =>
  check.count ? check.count(summary) : (summary.checks[check.key]?.count ?? 0);

export const isCheckApplicable = (
  check: CheckDefinition,
  summary: AssetInventorySummary,
) => (check.appliesTo ? check.appliesTo(summary) : true);

// Labels for the Details tab finding pills and the "finding" filter chips.
export const FINDING_LABELS: Record<string, string> = {
  pastEndOfSupport: 'Past end of support',
  lowDiskSpace: 'Disk nearly full',
  noBackup: 'No backup',
  serverBackup: 'Server backup overdue',
  staleBackup: 'Stale backup',
  vulnerabilities: 'Open vulnerabilities',
  serviceContractEnding: 'Contract expired or ending',
  noAntiVirus: 'No anti-virus',
  newDevice: 'New device',
  noMaintenanceWindow: 'No maintenance window',
  endOfSupportSoon: 'Support ends soon',
  endOfSupportNotMarked: 'Support date passed',
  noOperatingSystem: 'No OS recorded',
  noIpAddress: 'No IP address',
  notManaged: 'Not managed',
  noServiceContract: 'No service contract',
  notLinked: 'Not linked',
  unclearType: 'Unclear type',
  any: 'Has findings',
  backupIssue: 'Backup issue',
};

// These have their own column on the Details tab, so they aren't shown as finding pills.
export const INFORMATIONAL_FINDINGS = [
  'notManaged',
  'notLinked',
  'noServiceContract',
  'unclearType',
];

export const RED_FINDINGS = [
  'pastEndOfSupport',
  'vulnerabilities',
  'lowDiskSpace',
];
